import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { filterModelToFilterState } from './useServicesV4Data';
import { FilterState } from '../../components/molecules/FilterBar';
import { SavedReport } from '../../components/molecules/ReportTabs';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import { SavedReportTypeList } from '../../configs/enums';
import { useGetSavedReports, useGetReportByCode, useAddNewReport, useUpdateReportByCode } from '../../queries/report-query';

export interface SnackbarState {
  message: string;
  severity: 'success' | 'error' | 'info';
}

export interface UseReportsV4Return {
  savedReports: SavedReport[];
  reportsQuery: ReturnType<typeof useGetSavedReports>;
  activeReport: string | null;
  activeReportName: string | null;
  reportConfigQuery: ReturnType<typeof useGetReportByCode>;
  saveDialogOpen: boolean;
  snackbar: SnackbarState | null;
  handleReportChange: (code: string | null) => void;
  handleSaveNewReport: (name: string) => Promise<void>;
  handleOverwriteReport: () => Promise<void>;
  setSaveDialogOpen: (open: boolean) => void;
  setSnackbar: (state: SnackbarState | null) => void;
}

const EMPTY_FILTERS: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
};

const generateReportCode = (name: string) =>
  `${name.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}`;

interface UseReportsV4Options {
  filterModel: { items: GridFilterItem[] };
  pageSize: number;
  sortModel: { field: string; sort: string | null | undefined };
  columnSelectionData: ColumnSelectionItem[];
  applyFilters: (filters: FilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
  ) => void;
  setFilters: (filters: FilterState) => void;
  setFilterBarKey: React.Dispatch<React.SetStateAction<number>>;
}

export const useReportsV4 = ({
  filterModel,
  pageSize,
  sortModel,
  columnSelectionData,
  applyFilters,
  applyReportConfig,
  setFilters,
  setFilterBarKey,
}: UseReportsV4Options): UseReportsV4Return => {
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const addNewReport = useAddNewReport();
  const updateReport = useUpdateReportByCode(activeReport ?? '');

  // Fetch saved reports for the Service list
  const reportsQuery = useGetSavedReports({
    filterModel: { items: [{ field: 'pageName', operator: 'equals', value: 'ServiceList' }] },
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    reportsQuery.refetch();
  }, []);

  const savedReports: SavedReport[] = useMemo(() => {
    if (!reportsQuery.data?.data) return [];
    return reportsQuery.data.data.map((r) => ({
      code: r.code,
      name: r.name,
      type: (r.savedReportAccess?.length > 0 ? 'shared' : 'my') as 'my' | 'shared',
    }));
  }, [reportsQuery.data]);

  // Fetch and apply report config when a report tab is selected
  const reportConfigQuery = useGetReportByCode(activeReport ?? '');

  const handleReportChange = useCallback((code: string | null) => {
    setActiveReport(code);
    if (code === null) {
      // Reset to default "All Open Jobs"
      setFilters(EMPTY_FILTERS);
      setFilterBarKey((k) => k + 1);
      applyFilters(EMPTY_FILTERS);
    }
  }, [applyFilters, setFilters, setFilterBarKey]);

  useEffect(() => {
    if (activeReport) {
      reportConfigQuery.refetch();
    }
  }, [activeReport]);

  useEffect(() => {
    if (reportConfigQuery.data && activeReport) {
      const { dataGridProperties, columnSelection } = reportConfigQuery.data;
      applyReportConfig(
        dataGridProperties.filterModel,
        columnSelection?.length ? columnSelection : undefined,
      );
      const derivedFilters = filterModelToFilterState(dataGridProperties.filterModel);
      setFilters(derivedFilters);
      setFilterBarKey((k) => k + 1);
    }
  }, [reportConfigQuery.data]);

  const activeReportName = useMemo(
    () => savedReports.find((r) => r.code === activeReport)?.name ?? null,
    [savedReports, activeReport],
  );

  const handleSaveNewReport = useCallback(async (name: string) => {
    try {
      await addNewReport.mutateAsync({
        name,
        code: generateReportCode(name),
        reportTypeName: SavedReportTypeList.ServiceList,
        isDefault: false,
        dataGridProperties: { filterModel, pageSize, sortModel: [sortModel as GridSortItem] },
        columnSelection: columnSelectionData,
      });
      reportsQuery.refetch();
      setSnackbar({ message: 'Report saved', severity: 'success' });
    } catch {
      setSnackbar({ message: 'Failed to save report', severity: 'error' });
    }
  }, [addNewReport, filterModel, pageSize, sortModel, columnSelectionData, reportsQuery]);

  const handleOverwriteReport = useCallback(async () => {
    if (!activeReport) return;
    try {
      await updateReport.mutateAsync({
        dataGridProperties: { filterModel, pageSize, sortModel: [sortModel as GridSortItem] },
        columnSelection: columnSelectionData,
      });
      setSnackbar({ message: 'Report updated', severity: 'success' });
    } catch {
      setSnackbar({ message: 'Failed to update report', severity: 'error' });
    }
  }, [activeReport, updateReport, filterModel, pageSize, sortModel, columnSelectionData]);

  return {
    savedReports,
    reportsQuery,
    activeReport,
    activeReportName,
    reportConfigQuery,
    saveDialogOpen,
    snackbar,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    setSaveDialogOpen,
    setSnackbar,
  };
};
